/**
 * Unified input: keyboard, on-screen virtual joystick, and gamepad all collapse
 * into one action state that gameplay reads. Scenes never touch DOM events, so
 * the same movement code serves desktop keys and mobile thumb-drag identically.
 *
 * Canonical actions come straight from the design doc: a joystick for direction,
 * A to collect/interact, B to return/cancel.
 */

export interface ActionState {
  /** Normalized movement vector; x = right, y = forward(up on screen). */
  moveX: number;
  moveY: number;
  /** True on the frame the button went down. */
  aPressed: boolean;
  bPressed: boolean;
  pausePressed: boolean;
  /** True while held. */
  aHeld: boolean;
  bHeld: boolean;
  /** Which device produced the most recent input — drives HUD hint glyphs. */
  lastSource: 'keyboard' | 'touch' | 'gamepad';
}

const KEY_MAP: Record<string, keyof typeof AXIS | 'a' | 'b' | 'pause'> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyA: 'left',
  KeyD: 'right',
  Space: 'a',
  Enter: 'a',
  KeyE: 'a',
  KeyZ: 'a',
  Escape: 'pause',
  KeyP: 'pause',
  Backspace: 'b',
  KeyX: 'b',
  KeyQ: 'b',
};

const AXIS = { up: 1, down: 1, left: 1, right: 1 } as const;

export class Input {
  readonly state: ActionState = {
    moveX: 0,
    moveY: 0,
    aPressed: false,
    bPressed: false,
    pausePressed: false,
    aHeld: false,
    bHeld: false,
    lastSource: 'keyboard',
  };

  private keys = new Set<string>();
  private edgeA = false;
  private edgeB = false;
  private edgePause = false;
  private prevGamepadA = false;
  private prevGamepadB = false;
  /**
   * Whether the ON-SCREEN A / B buttons are currently down.
   *
   * Held separately from `state.aHeld` because the two sources have to be OR-ed
   * fresh every frame. Folding the touch state into the published flag and then
   * OR-ing that flag with the keyboard — which is what this class used to do —
   * makes the flag monotonic: once anything sets it true it can never go back to
   * false, because the previous frame's `true` is one of the operands.
   *
   * That latch was invisible for as long as no scene read `aHeld`. Game 2's line
   * run is the first to hold a button (hold A to fire the burner, release to cut
   * the dryer), and under it the burner could never be turned off.
   */
  private touchA = false;
  private touchB = false;

  /** Joystick vector supplied by the DOM control, already normalized. */
  private stick = { x: 0, y: 0, active: false };

  private disposers: Array<() => void> = [];
  private enabled = true;

  constructor(private readonly target: HTMLElement | Window = window) {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!this.enabled) return;
      const action = KEY_MAP[e.code];
      if (!action) return;
      e.preventDefault();
      this.state.lastSource = 'keyboard';
      if (this.keys.has(e.code)) return; // ignore auto-repeat for edges
      this.keys.add(e.code);
      if (action === 'a') this.edgeA = true;
      else if (action === 'b') this.edgeB = true;
      else if (action === 'pause') this.edgePause = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
      this.keys.delete(e.code);
    };
    // Losing focus mid-hold would otherwise leave the player walking forever.
    const onBlur = () => this.keys.clear();

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    this.disposers.push(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    });
    void this.target;
  }

  /** Called by the virtual joystick component each pointer move. */
  setStick(x: number, y: number): void {
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    this.stick.x = x;
    this.stick.y = y;
    this.stick.active = len > 0.01;
    if (this.stick.active) this.state.lastSource = 'touch';
  }

  releaseStick(): void {
    this.stick.x = 0;
    this.stick.y = 0;
    this.stick.active = false;
  }

  /** Called by the on-screen A/B buttons. */
  pressButton(which: 'a' | 'b' | 'pause'): void {
    if (!this.enabled) return;
    this.state.lastSource = 'touch';
    if (which === 'a') {
      this.edgeA = true;
      this.touchA = true;
    } else if (which === 'b') {
      this.edgeB = true;
      this.touchB = true;
    } else this.edgePause = true;
  }

  releaseButton(which: 'a' | 'b'): void {
    if (which === 'a') this.touchA = false;
    else this.touchB = false;
  }

  /** Suspend gameplay input while a modal owns the screen. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.releaseAll();
  }

  /** Clear held controls without changing a scene's existing input lock. */
  releaseAll(): void {
    this.keys.clear();
    this.releaseStick();
    this.edgeA = this.edgeB = this.edgePause = false;
    this.touchA = this.touchB = false;
  }

  /**
   * Resolve one frame of input. Must be called exactly once per frame, before
   * gameplay reads `state`, because it consumes the press edges.
   */
  update(): ActionState {
    const s = this.state;

    let mx = 0;
    let my = 0;
    if (this.keys.has('ArrowLeft') || this.keys.has('KeyA')) mx -= 1;
    if (this.keys.has('ArrowRight') || this.keys.has('KeyD')) mx += 1;
    if (this.keys.has('ArrowUp') || this.keys.has('KeyW')) my += 1;
    if (this.keys.has('ArrowDown') || this.keys.has('KeyS')) my -= 1;
    if (mx !== 0 || my !== 0) {
      // Normalize so diagonals aren't 41% faster than cardinals.
      const len = Math.hypot(mx, my);
      mx /= len;
      my /= len;
    }

    if (this.stick.active) {
      mx = this.stick.x;
      my = this.stick.y;
    }

    this.pollGamepad((gx, gy) => {
      if (Math.hypot(gx, gy) > 0.15) {
        mx = gx;
        my = gy;
        s.lastSource = 'gamepad';
      }
    });

    s.moveX = this.enabled ? mx : 0;
    s.moveY = this.enabled ? my : 0;
    // Recomputed from scratch every frame, never OR-ed with its own last value.
    s.aHeld = this.touchA || this.keys.has('Space') || this.keys.has('Enter') || this.keys.has('KeyE');
    s.bHeld = this.touchB || this.keys.has('KeyX');

    s.aPressed = this.edgeA;
    s.bPressed = this.edgeB;
    s.pausePressed = this.edgePause;
    this.edgeA = this.edgeB = this.edgePause = false;

    return s;
  }

  private pollGamepad(apply: (x: number, y: number) => void): void {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return;
    const pads = navigator.getGamepads();
    for (const pad of pads) {
      if (!pad) continue;
      const ax = pad.axes[0] ?? 0;
      const ay = pad.axes[1] ?? 0;
      // Deadzone, then invert Y so "stick up" is "forward".
      apply(Math.abs(ax) > 0.15 ? ax : 0, Math.abs(ay) > 0.15 ? -ay : 0);

      const a = !!pad.buttons[0]?.pressed;
      const b = !!pad.buttons[1]?.pressed;
      if (a && !this.prevGamepadA) this.edgeA = true;
      if (b && !this.prevGamepadB) this.edgeB = true;
      this.prevGamepadA = a;
      this.prevGamepadB = b;
      break;
    }
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
  }
}
